import { useContext, useState, ReactNode, FC } from 'react'
import { useResizeDetector } from 'react-resize-detector'
import {
  useFloating,
  shift,
  autoUpdate,
  useInteractions,
  useClick,
  useDismiss,
  FloatingPortal,
} from '@floating-ui/react'
import useDynamicAuthorization from '../../../hooks/useDynamicAuthorization'
import Status from '../../../constants/request_statuses'
import HorizontalSpinner from '../../../components/HorizontalSpinner'
import { AbilityContext } from '../../../can'
import DotMenu from '../../../components/DotMenu'
import { ActionObjectType, ResourceActionsProps } from '../types'
import { ContextualAbilityType } from '@app-types/general'

const ResourceActions: FC<ResourceActionsProps> = ({
  resourceId,
  resourceType,
  title = 'Actions',
  actionObjects,
  hideOnActionDone = true,
  customTrigger,
}) => {
  // static abilities
  const ability: ContextualAbilityType = useContext(AbilityContext)
  if (!ability) {
    throw new Error('AbilityContext unavailable, ensure CanProvider is wrapping the component')
  }
  if (ability.can === undefined) {
    throw new Error("Casl's can unavailable via context.")
  }

  const [popoverVisible, setPopoverVisible] = useState(false)
  const { width: popoverContentWidth, height: popoverContentHeight, ref: popoverContentRef } = useResizeDetector()

  // dynamic abilities
  const {
    dynamicAbilities,
    can,
    status: abilitiesStatus,
    error: abilitiesError,
    refreshAbilities,
  } = useDynamicAuthorization({
    resourceId,
    resourceType,
    abilities: actionObjects.map(action => action.ability),
  })

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      hidePopover()
    }
  }

  const { refs, floatingStyles, context } = useFloating({
    open: popoverVisible,
    onOpenChange: handleOpenChange,
    middleware: [
      shift({
        crossAxis: true, // allows overlapping reference
        padding: 16, // 16px from window edges
      }),
    ],
    whileElementsMounted: autoUpdate,
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([useClick(context), useDismiss(context)])

  const showPopover = () => {
    refreshAbilities()
    setPopoverVisible(true)
  }

  const hidePopover = () => {
    setPopoverVisible(false)
  }

  const togglePopoverVisibility = () => {
    if (popoverVisible) {
      hidePopover()
    } else {
      showPopover()
    }
  }

  const handleActionDone = () => {
    if (hideOnActionDone) {
      hidePopover()
    }
  }

  // Abstracting the logic to determine if an action is permitted and returning the JSX.
  const createActionItem = (
    actionObject: ActionObjectType,
    ability: ContextualAbilityType,
    _dynamicAbilities: object
  ): ReactNode => {
    if (!actionObject) {
      return null
    }
    const abilityString: string = actionObject.ability
    const isPermitted = (() => {
      switch (abilityString) {
        case 'publish':
          // exit early if current user doesn't have publishing capabilities (using casl)
          if (!ability.can('publish')) {
            return false
          }
          // now check if they have publishing capabilities on this specific resource (using the dynamic ability)
          return can('publish')
        case 'share':
          return true // share is always available
        default:
          return can(abilityString)
      }
    })()

    if (!isPermitted) {
      return null
    }

    const ActionComponent = actionObject.component as FC<ActionObjectType['props']>
    const actionProps = { ...actionObject.props, onDone: handleActionDone }

    return (
      <li className="nav-item" key={actionObject.key}>
        <ActionComponent {...actionProps} />
      </li>
    )
  }

  const getActionItems = (
    actionObjects: ActionObjectType[],
    ability: ContextualAbilityType,
    dynamicAbilities: object
  ) => {
    return actionObjects.map(action => createActionItem(action, ability, dynamicAbilities)).filter(Boolean)
  }

  const getPopoverContent = () => {
    if (popoverVisible) {
      let content
      if (abilitiesStatus === Status.SUCCEEDED) {
        const items = getActionItems(actionObjects, ability, dynamicAbilities)
        content = items.length > 0 ? <ul className="unstyled-list">{items}</ul> : <div>No actions available</div>
      } else if (abilitiesStatus === Status.FAILED) {
        content = <div>{abilitiesError.message}</div>
      } else {
        content = (
          <div className="progress-container">
            <HorizontalSpinner speed="fast" />
          </div>
        )
      }

      const popoverContentContainerStyle = {
        width: popoverContentWidth,
        height: popoverContentHeight,
        transition: 'width 150ms ease, height 150ms ease',
        overflow: 'hidden',
      }
      // If we might show publish switch, set a larger width so the popover doesn't glitch when content is loaded
      // we also set width instead of minwidth because otherwise publish switch error message cause width to expand
      // const predicate = (actionObject: ActionObjectType) => {
      //   const abilityToCheck = get(actionObject, 'ability')
      //   return abilityToCheck === 'publish'
      // }
      //const anyPublishActions: boolean = some(actionObjects, predicate)
      //const mightShowPublishSwitch = anyPublishActions && ability.can('publish')
      const popoverContentStyle = {
        width: '150px', //mightShowPublishSwitch ? '150px' : '85px',
      }

      return (
        <FloatingPortal id="floating-portal-target">
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="popover popover--menu qa-actions-menu"
            {...getFloatingProps()}
          >
            <div style={popoverContentContainerStyle}>
              <div ref={popoverContentRef} style={popoverContentStyle}>
                {content}
              </div>
            </div>
          </div>
        </FloatingPortal>
      )
    }
  }

  const popoverContent = getPopoverContent()
  const buttonAriaLabel = popoverVisible ? `Less actions` : `More actions`
  const Trigger = customTrigger ? customTrigger : DotMenu

  return (
    <>
      <div className="actions-menu-trigger-container" ref={refs.setReference} {...getReferenceProps()}>
        <Trigger
          className="button-reset qa-menu-trigger"
          onClick={togglePopoverVisibility}
          ariaLabel={buttonAriaLabel}
          title={title}
        />
      </div>
      {popoverContent}
    </>
  )
}

export default ResourceActions
